import {
    IFilter,
    BaseFilterValue,
    IFilterHelper,
    FilterAttribute,
    Field,
    getLabel,
    IEntityFilterHelper,
    IActiveFilter
} from "../../entities/Metadata";
import { Dictionary } from "../../entities/common";
import { DisplayFieldService } from "../../components/common/DisplayFieldService";
import { FieldsService } from "../../components/common/FieldsService";
import { ActiveFilter } from "../project/filters";
import { ApprovalRequest } from "../ApprovalCenterStore";

export type FieldCompareOverride<TItem = ApprovalRequest> = (
    item: TItem,
    field: Field,
    fieldFilterValue: any
) => boolean | undefined;

type FilterHelperProps = {
    fields: Field[];
    fieldValueExtractor?: (item: ApprovalRequest, field: Field) => any;
    fieldCompareOverride?: FieldCompareOverride;
};

export class FilterHelper implements IEntityFilterHelper<ApprovalRequest> {
    public getFilterAttributes = (fields: Field[]): FilterAttribute<BaseFilterValue>[] => {
        return fields.map(
            f =>
                ({
                    type: "attributes",
                    value: f,
                    name: f.name,
                    displayName: getLabel(f)
                }) as FilterAttribute<BaseFilterValue>
        );
    };

    private attributes: IFilterHelper<BaseFilterValue, ApprovalRequest> = {
        buildFilterElement: (
            attr: FilterAttribute<BaseFilterValue>,
            filter: IFilter<BaseFilterValue>,
            onFilterEditComplete: (type: string | number, name: string, value: any) => void
        ): JSX.Element | null => {
            const field: Field = attr.value;
            return DisplayFieldService.buildFieldMultiSelectInput(field, filter.value?.attributes?.[field.name], changed =>
                onFilterEditComplete(attr.type, field.name, changed)
            );
        },
        removeFilterAttribute: (attrName: string, typeValue: any) => {
            const newValue: Dictionary<any> = {};
            Object.keys(typeValue).forEach(vk => {
                if (vk !== attrName) {
                    newValue[vk] = typeValue[vk];
                }
            });
            return newValue;
        },
        setAttributeValue: (attrName: string, value: any, oldValue: any) => {
            if (!oldValue) {
                oldValue = {};
            }
            const tmpValue = { ...oldValue };
            tmpValue[attrName] = value;
            return tmpValue;
        },
        getAttributeValues: (value: any): string[] => FieldsService.getAttributeDisplayValues(this._props.fields, value),
        validateItem: (item: ApprovalRequest, filterValue: any, attributes: any[]): boolean => {
            if (!filterValue) {
                return false;
            }
            for (const key in filterValue) {
                if (filterValue.hasOwnProperty(key)) {
                    if (
                        filterValue[key] === undefined ||
                        (Array.isArray(filterValue[key]) && filterValue[key].length === 0)
                    ) {
                        continue;
                    }

                    const attribute = attributes.find(_ => _.name === key);
                    if (!attribute) {
                        return false;
                    }
                    const field = attribute.value;
                    const value =
                        field.isFake && this._props.fieldValueExtractor
                            ? this._props.fieldValueExtractor(item, field)
                            : item.attributes[key];

                    const overrideResult = this._props.fieldCompareOverride?.(item, field, filterValue[key]);
                    if (overrideResult !== undefined) {
                        if (!overrideResult) {
                            return false;
                        }
                    } else if (!FieldsService.compareFieldValues(field, value, filterValue[key])) {
                        return false;
                    }
                }
            }
            return true;
        }
    };

    private readonly _props: FilterHelperProps;
    constructor(props: FilterHelperProps) {
        this._props = props;
    }

    public newFilter = (name: string): IActiveFilter => new ActiveFilter(name);
    public helpersMap: { [K: string]: IFilterHelper<BaseFilterValue, ApprovalRequest> } = {
        attributes: this.attributes
    };

    public filter(requests: ApprovalRequest[], activeFilter?: IFilter<BaseFilterValue>): ApprovalRequest[] {
        if (!activeFilter) {
            return requests;
        }

        const filterValue = activeFilter.value;
        const allAttributes = this.getFilterAttributes(this._props.fields);

        return requests.filter(request => {
            for (const type in filterValue) {
                if (
                    !this.helpersMap[type].validateItem(
                        request,
                        filterValue[type],
                        allAttributes.filter(_ => _.type === type)
                    )
                ) {
                    return false;
                }
            }
            return true;
        });
    }
}
